import { DGE_AUTH_METHOD, PLATFORM } from '@/constants/platform'
import { ROUTE_URL } from '@/router/constants'
import { useAuthStore } from '@/stores/useAuthStore'
import logger from '@/utils/_legacy/logger'
import { notifyError } from '@/utils/notifyError'

import { KeycloakStateManager } from './KeycloakStateManager'
import { SessionManager } from './SessionManager'
import { TokenManager } from './TokenManager'

/**
 * Main Keycloak service handling authentication flow
 * Delegates specific responsibilities to specialized managers
 */
class KeycloakService {
  private stateManager = new KeycloakStateManager()
  private tokenManager: TokenManager
  private sessionManager: SessionManager

  constructor() {
    this.tokenManager = new TokenManager({
      onTokenRefreshed: (token) => this.sessionManager.updateToken(token),
      onSessionExpired: () => this.handleSessionExpired()
    })

    this.sessionManager = new SessionManager({
      onSessionSetupComplete: () => this.startSessionMonitoring()
    })
  }

  async initializeOnAppLoad(): Promise<void> {
    const existingPromise = this.stateManager.getInitializationPromise()
    if (existingPromise) {
      return existingPromise
    }

    const promise = this.doInitialization()
    this.stateManager.setInitializationPromise(promise)

    return promise
  }

  async login(): Promise<void> {
    try {
      if (this.stateManager.isActive()) {
        this.cleanup()
      }

      const keycloak = this.stateManager.getInstance()
      const authenticated = await keycloak.init(this.stateManager.getInitConfig(true))

      if (!authenticated) {
        this.stateManager.setLoginInProgress()
        await keycloak.login({ redirectUri: this.getLoginRedirectUri() })
        return
      }

      await this.setupAuthenticatedSession(keycloak)
    } catch (error) {
      this.handleLoginError(error)
      throw error
    }
  }

  async logout(): Promise<void> {
    this.stopSessionMonitoring()

    const keycloak = this.stateManager.isActive() ? this.stateManager.getInstance() : null

    if (keycloak) {
      try {
        if (keycloak.authenticated) {
          await keycloak.logout({ redirectUri: this.getLogoutRedirectUri() })
        } else {
          keycloak.clearToken()
          this.sessionManager.clearAuthData()
          window.location.href = this.getLogoutRedirectUri()
        }
      } catch (error) {
        this.handleLogoutError()
      } finally {
        this.cleanup()
      }
    } else {
      this.sessionManager.clearAuthData()
      this.cleanup()
    }
  }

  isActive(): boolean {
    return this.stateManager.isActive()
  }

  private async doInitialization(): Promise<void> {
    const authState = useAuthStore.getState()

    if (authState.platform !== PLATFORM.DGE) {
      return
    }

    const hasCallback = this.stateManager.hasKeycloakCallbackParams()
    const isKeycloakAuth = authState.dgeAuthMethod === DGE_AUTH_METHOD.KEYCLOAK
    const loginInProgress = this.stateManager.isLoginInProgress()

    if (!hasCallback && !isKeycloakAuth && !loginInProgress) {
      return
    }

    if (this.stateManager.isActive() && !authState.isAuthenticated) {
      this.cleanup()
    }

    const keycloak = this.stateManager.getInstance()

    try {
      const authenticated = await keycloak.init(this.stateManager.getInitConfig(false))

      if (authenticated && keycloak.token) {
        this.stateManager.clearLoginInProgress()
        await this.sessionManager.setupSession(keycloak, hasCallback)
      } else {
        this.handleUnauthenticatedInit(loginInProgress)
      }
    } catch (error) {
      this.handleInitializationError(error, hasCallback, loginInProgress)
    }
  }

  private async setupAuthenticatedSession(keycloak: import('keycloak-js').default): Promise<void> {
    const success = await this.sessionManager.setupSession(keycloak, false)

    if (!success) {
      throw new Error('Failed to setup user session')
    }
  }

  private startSessionMonitoring(): void {
    const keycloak = this.stateManager.getInstance()
    this.tokenManager.start(keycloak)
  }

  private stopSessionMonitoring(): void {
    this.tokenManager.stop()
  }

  private async handleSessionExpired(): Promise<void> {
    logger.info('Session expired - performing full Keycloak logout')
    this.stopSessionMonitoring()
    this.sessionManager.clearAuthData()

    if (this.stateManager.isActive()) {
      const keycloak = this.stateManager.getInstance()
      try {
        await keycloak.logout({ redirectUri: this.getLogoutRedirectUri() })
      } catch (error) {
        logger.error(error, 'Error during Keycloak logout on session expiration')
        try {
          keycloak.clearToken()
        } catch (clearError) {
          logger.error(clearError, 'Error clearing token after logout failure')
        }
        this.cleanup()
        window.location.href = this.getLogoutRedirectUri()
      }
    } else {
      window.location.href = this.getLogoutRedirectUri()
    }
  }

  private handleUnauthenticatedInit(loginInProgress: boolean): void {
    const authState = useAuthStore.getState()

    if (authState.isAuthenticated || authState.token) {
      authState.clearAuthData()
    }

    if (loginInProgress) {
      this.stateManager.clearLoginInProgress()
    }

    this.cleanup()
  }

  private handleInitializationError(
    error: unknown,
    hasCallback: boolean,
    loginInProgress: boolean
  ): void {
    this.stateManager.clearLoginInProgress()

    if (hasCallback || loginInProgress) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to complete authentication'
      notifyError(errorMessage)
    }

    this.sessionManager.clearAuthData()
    this.cleanup()
  }

  private handleLoginError(error: unknown): void {
    const errorMessage =
      error instanceof Error ? error.message : 'An unexpected error occurred during login'

    if (!errorMessage.includes('Access denied')) {
      notifyError(errorMessage)
    }

    this.cleanup()
    this.sessionManager.clearAuthData()
  }

  private handleLogoutError(): void {
    const keycloak = this.stateManager.isActive() ? this.stateManager.getInstance() : null
    if (keycloak) {
      try {
        keycloak.clearToken()
      } catch (clearError) {
        logger.error(clearError, 'Error clearing Keycloak token on logout')
      }
    }

    this.sessionManager.clearAuthData()
    window.location.href = this.getLogoutRedirectUri()
  }

  private cleanup(): void {
    this.stopSessionMonitoring()
    this.stateManager.cleanup()
  }

  private getLoginRedirectUri(): string {
    return `${window.location.origin}${ROUTE_URL.OPERATORS.DEFAULT}`
  }

  private getLogoutRedirectUri(): string {
    return `${window.location.origin}${ROUTE_URL.LOGIN}`
  }
}

export const keycloakService = new KeycloakService()
